import { action } from 'mobx';
import { HubModuleType, ICreateEventsHubModuleDTO, IEventsHubModuleDTO, TCreateHubModuleDTO, } from '../../../../../../../../api/event-hubs.dao';
import { IEventAttributeDetailsDTO } from '../../../../../../../../api/attributes.dao';
import {
    DEFAULT_PAST_EVENTS_DAYS,
    DEFAULT_UPCOMING_EVENTS_DAYS,
    DISPLAY_PAST_EVENTS,
    DISPLAY_UPCOMING_EVENTS,
    EVENTS_MODULE_CONFIG,
    PAST_EVENTS_DAYS,
    UPCOMING_EVENTS_DAYS,
} from './events-module.config';
import { ModuleAttributesVM } from '../_module-attributes/module-attributes.viewmodel';
import { EventsBaseModuleVM } from '../_events-base/events-base-module.viewmodel';

export class EventsModuleVM extends EventsBaseModuleVM {
    private readonly eventAttributeOptions: IEventAttributeDetailsDTO[];
    private moduleAttributesVM: ModuleAttributesVM;

    public static createEmpty(positionOrder: number, eventAttributeOptions: IEventAttributeDetailsDTO[]): EventsModuleVM {
        return new EventsModuleVM({ positionOrder: positionOrder }, eventAttributeOptions);
    }

    public static createFromDTO(dto: IEventsHubModuleDTO | ICreateEventsHubModuleDTO, eventAttributeOptions: IEventAttributeDetailsDTO[]): EventsModuleVM {
        const initialData = EventsModuleVM.fromEventsBaseModuleDTO(dto);
        return new EventsModuleVM(initialData, eventAttributeOptions);
    }

    private constructor(private readonly module: Partial<IEventsHubModuleDTO>, eventAttributeOptions: IEventAttributeDetailsDTO[]) {
        super(module.moduleId, 'EventsModuleVM', EVENTS_MODULE_CONFIG, module);
        this.eventAttributeOptions = eventAttributeOptions;
        this.moduleAttributesVM = ModuleAttributesVM.createFromDTO(module.attributes ?? [], eventAttributeOptions);
        this.reconcileLegacyEventsDisplayConfig();
        this.savedSnapshot = this.serialize();
    }

    public getModuleType = (): HubModuleType => {
        return HubModuleType.EVENTS;
    };

    public getModuleAttributesVM = (): ModuleAttributesVM => {
        return this.moduleAttributesVM;
    };

    public isValid(): boolean {
        return super.isValid() && this.moduleAttributesVM.valid;
    }

    @action
    public setDisplayUpcomingEvents = (displayed: boolean): void => {
        this.setValue(DISPLAY_UPCOMING_EVENTS, displayed);
        if (displayed) {
            this.setValue(DISPLAY_PAST_EVENTS, false);
            this.setValue(PAST_EVENTS_DAYS, DEFAULT_PAST_EVENTS_DAYS);
        }
    };

    @action
    public setDisplayPastEvents = (displayed: boolean): void => {
        this.setValue(DISPLAY_PAST_EVENTS, displayed);
        if (displayed) {
            this.setValue(DISPLAY_UPCOMING_EVENTS, false);
            this.setValue(UPCOMING_EVENTS_DAYS, DEFAULT_UPCOMING_EVENTS_DAYS);
        }
    };

    @action
    public markTouched = (): void => {
        this.setVmTouched();
        this.moduleAttributesVM.setTouched();
    };

    public toCreateDTO = (): ICreateEventsHubModuleDTO => {
        const eventsBaseModuleDTO = this.toEventsBaseModuleDTO();

        return {
            ...eventsBaseModuleDTO,
            moduleType: HubModuleType.EVENTS,
            attributes: this.moduleAttributesVM.toDTO(),
        };
    };

    protected cloneFromDTO = (dto: TCreateHubModuleDTO): EventsModuleVM => {
        const eventsModuleDTO = dto as ICreateEventsHubModuleDTO;
        return EventsModuleVM.createFromDTO(eventsModuleDTO, this.eventAttributeOptions);
    };

    // Legacy records may have both flags true; Upcoming wins, Past is treated as disabled.
    private reconcileLegacyEventsDisplayConfig = (): void => {
        if (this.getBooleanValue(DISPLAY_UPCOMING_EVENTS) && this.getBooleanValue(DISPLAY_PAST_EVENTS)) {
            this.setValue(DISPLAY_PAST_EVENTS, false);
            this.setValue(PAST_EVENTS_DAYS, DEFAULT_PAST_EVENTS_DAYS);
        }
    };
}
